import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';
import { TagJustificationSection, LinkSection } from './PostCreationComponents';
import { handleAddTagFeedback, handleAddComment } from './dataHandlers';

// Create a custom sanitize schema that allows images
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    img: ['src', 'alt', 'title', 'width', 'height', 'className']
  },
  tagNames: [...(defaultSchema.tagNames || []), 'img']
};

// Custom hook for managing post editing state
export const usePostEditing = () => {
  const [editingPostId, setEditingPostId] = useState(null);
  const [editPostForm, setEditPostForm] = useState({
    title: '',
    content: '',
    projectDescription: '',
    projectType: 'individual',
    links: [],
    tagJustifications: [],
    associatedClass: ''
  });
  const [newTagJustificationEdit, setNewTagJustificationEdit] = useState({ tag: '', justification: '' });
  const [newLinkEdit, setNewLinkEdit] = useState({ title: '', url: '' });

  const handleEditPost = (post) => {
    setEditingPostId(post.id);
    setEditPostForm({
      title: post.title || '',
      content: post.content || '',
      projectDescription: post.projectDescription || '',
      projectType: post.projectType || 'individual',
      links: post.links || [],
      tagJustifications: post.tagJustifications || [],
      associatedClass: post.associatedClass || ''
    });
    setNewTagJustificationEdit({ tag: '', justification: '' });
    setNewLinkEdit({ title: '', url: '' });
  };

  const handleAddTagJustificationEdit = () => {
    if (!newTagJustificationEdit.tag.trim() || !newTagJustificationEdit.justification.trim()) {
      alert('Please provide both a tag and justification.');
      return;
    }

    setEditPostForm(prevForm => ({
      ...prevForm,
      tagJustifications: [...prevForm.tagJustifications, { 
        tag: newTagJustificationEdit.tag.trim(), 
        justification: newTagJustificationEdit.justification.trim() 
      }]
    }));

    setNewTagJustificationEdit({ tag: '', justification: '' });
  };

  const handleRemoveTagJustificationEdit = (index) => {
    setEditPostForm(prevForm => ({
      ...prevForm,
      tagJustifications: prevForm.tagJustifications.filter((_, i) => i !== index)
    }));
  };

  const handleAddLinkEdit = () => {
    if (!newLinkEdit.title.trim() || !newLinkEdit.url.trim()) {
      alert('Please provide both a title and URL for the link.');
      return;
    }

    // Normalize URL - add https:// if no protocol is specified
    let normalizedUrl = newLinkEdit.url.trim();
    if (!normalizedUrl.match(/^https?:\/\//i)) {
      normalizedUrl = 'https://' + normalizedUrl;
    }

    // Validate URL format
    try {
      new URL(normalizedUrl);
    } catch (error) {
      alert('Please provide a valid URL (e.g., example.com or www.example.com)');
      return;
    }

    // Check if we already have 5 links
    if (editPostForm.links.length >= 5) {
      alert('You can only add up to 5 links per post.');
      return;
    }

    setEditPostForm(prevForm => ({
      ...prevForm,
      links: [...prevForm.links, { 
        title: newLinkEdit.title.trim(), 
        url: normalizedUrl 
      }]
    }));

    setNewLinkEdit({ title: '', url: '' });
  };

  const handleRemoveLinkEdit = (index) => {
    setEditPostForm(prevForm => ({
      ...prevForm,
      links: prevForm.links.filter((_, i) => i !== index)
    }));
  };

  const handleSaveEditPost = async (setPosts) => {
    if (!editPostForm.content.trim() || !editPostForm.projectDescription.trim()) {
      alert('Please provide both a reflection and project description.');
      return;
    }

    try {
      const postRef = doc(db, 'posts', editingPostId);
      await updateDoc(postRef, {
        title: editPostForm.title,
        content: editPostForm.content,
        projectDescription: editPostForm.projectDescription,
        projectType: editPostForm.projectType,
        links: editPostForm.links,
        tagJustifications: editPostForm.tagJustifications,
        associatedClass: editPostForm.associatedClass || null
      });

      // Update local state
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === editingPostId
            ? { ...post, ...editPostForm }
            : post
        )
      );

      setEditingPostId(null);
      setEditPostForm({
        title: '',
        content: '',
        projectDescription: '',
        projectType: 'individual',
        links: [],
        tagJustifications: [],
        associatedClass: ''
      });
      setNewTagJustificationEdit({ tag: '', justification: '' });
      setNewLinkEdit({ title: '', url: '' });
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Failed to update post. Please try again.');
    }
  };

  const handleCancelEditPost = () => {
    setEditingPostId(null);
    setEditPostForm({
      title: '',
      content: '',
      projectDescription: '',
      projectType: 'individual',
      links: [],
      tagJustifications: [],
      associatedClass: ''
    });
    setNewTagJustificationEdit({ tag: '', justification: '' });
    setNewLinkEdit({ title: '', url: '' });
  };

  return {
    editingPostId,
    editPostForm,
    setEditPostForm,
    newTagJustificationEdit,
    setNewTagJustificationEdit,
    newLinkEdit,
    setNewLinkEdit,
    handleEditPost,
    handleAddTagJustificationEdit,
    handleRemoveTagJustificationEdit,
    handleAddLinkEdit,
    handleRemoveLinkEdit,
    handleSaveEditPost,
    handleCancelEditPost
  };
};

// Post action handlers
export const postActionHandlers = {
  handleToggleFeatured: async (postId, currentFeatured, posts, currentUser, setPosts) => {
    try {
      // Enforce per-student limit: max 10 featured posts
      if (!currentFeatured) {
        const thisPost = posts.find(p => p.id === postId);
        const ownerId = thisPost?.userId || currentUser?.uid;
        const featuredCount = (posts || []).filter(p => p.userId === ownerId && p.featured).length;
        if (featuredCount >= 10) {
          alert('You can only feature up to 10 projects. Please unfeature another project first.');
          return;
        }
      }

      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        featured: !currentFeatured
      });
      
      // Update local state
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, featured: !currentFeatured }
            : post
        )
      );
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to update featured status');
    }
  },

  handleToggleHideFromTranscript: async (postId, currentHidden, setPosts) => {
    try {
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        hideFromTranscript: !currentHidden
      });
      
      // Update local state
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, hideFromTranscript: !currentHidden }
            : post
        )
      );
    } catch (error) {
      console.error('Error toggling transcript visibility:', error);
      alert('Failed to update transcript visibility');
    }
  },

  handleDeletePost: async (postId, setPosts) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete the post from Firestore
      await deleteDoc(doc(db, 'posts', postId));

      // Update local state
      setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
      
      alert('Post deleted successfully');
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again.');
    }
  }
};

// Post feedback handlers - now using imported handlers
export const postFeedbackHandlers = {
  handleAddTagFeedback: (postId, tag, newTagFeedback, setNewTagFeedback, currentUser, userProfile, tagFeedbacks, posts) => {
    return handleAddTagFeedback({
      postId,
      tag,
      newTagFeedback,
      setNewTagFeedback,
      currentUser,
      userProfile,
      tagFeedbacks,
      posts
    });
  },

  handleAddComment: (postId, newComment, setNewComment, currentUser, userProfile) => {
    return handleAddComment({
      postId,
      newComment,
      setNewComment,
      currentUser,
      userProfile
    });
  }
};

// Individual post display component
export const PostItem = ({ 
  post,
  currentUser,
  currentUserRole,
  isPostGraded,
  getPostTagFeedbacks,
  getPostComments,
  newTagFeedback,
  setNewTagFeedback,
  newComment,
  setNewComment,
  teacherExtraFeedback,
  setTeacherExtraFeedback,
  tags,
  studentClasses,
  classTypes,
  highlightedPostId,
  // Editing state
  editingPostId,
  editPostForm,
  setEditPostForm,
  newTagJustificationEdit,
  setNewTagJustificationEdit,
  newLinkEdit,
  setNewLinkEdit,
  // Feedback editing state
  editingFeedbackId,
  editingFeedbackForm,
  setEditingFeedbackId,
  setEditingFeedbackForm,
  // Expansion state
  isExpanded,
  onToggleExpansion,
  // Action handlers
  onToggleFeatured,
  onToggleHideFromTranscript,
  onEditPost,
  onDeletePost,
  onSaveEditPost,
  onCancelEditPost,
  onAddTagJustificationEdit,
  onRemoveTagJustificationEdit,
  onAddLinkEdit,
  onRemoveLinkEdit,
  onAddTagFeedback,
  onAddComment,
  onEditFeedback,
  onSaveFeedback,
  onCancelEditFeedback,
  onDeleteFeedback
}) => {
  return (
    <div 
      id={`post-${post.id}`}
      className={`post post-margin ${highlightedPostId === post.id ? 'highlighted' : ''} ${isExpanded ? 'expanded' : 'collapsed'}`}
    >
      <div className="post-header">
        <div>
          <strong>{post.username}</strong>
          {post.projectType && (
            <div className="post-project-type">
              {post.projectType === 'individual' ? 'Individual Project' : 'Group Project'}
            </div>
          )}
          {post.title && <div className="post-title">{post.title}</div>}
        </div>
        <div className="post-header-actions">
          {currentUser && post.userId === currentUser.uid && (
            <>
              <button
                className={`star-button ${post.featured ? 'starred' : ''}`}
                onClick={() => onToggleFeatured(post.id, post.featured)}
                title={post.featured ? 'Remove from featured' : 'Add to featured'}
              >
                <i className={`fa-solid ${post.featured ? 'fa-star' : 'fa-star'}`}></i>
              </button>
              <button
                className={`hide-button ${post.hideFromTranscript ? 'hidden' : ''}`}
                onClick={() => onToggleHideFromTranscript(post.id, post.hideFromTranscript)}
                title={post.hideFromTranscript ? 'Show on transcript' : 'Hide from transcript'}
              >
                <i className={`fa-solid ${post.hideFromTranscript ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
              {!isPostGraded(post.id) && (
                <>
                  <button
                    className="proposal-edit-btn"
                    onClick={() => onEditPost(post)}
                    title="Edit post"
                  >
                    Edit
                  </button>
                  <button
                    className="proposal-reject-btn"
                    onClick={() => onDeletePost(post.id)}
                    title="Delete post"
                  >
                    Delete
                  </button>
                </>
              )}
            </>
          )}
          <span className="timestamp">{new Date(post.timestamp).toLocaleString()}</span>
        </div>
      </div>
      
      {/* Toggle expansion button - only show when not editing */}
      {editingPostId !== post.id && (
        <button 
          className="post-toggle-button"
          onClick={() => onToggleExpansion(post.id)}
          title={isExpanded ? "Collapse post" : "Expand post"}
        >
          <i className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
        </button>
      )}

      {/* Only show content when expanded or editing */}
      {editingPostId === post.id ? (
        // Edit form
        <div className="edit-post-form">
          <input
            type="text"
            placeholder="Title (optional)"
            value={editPostForm.title}
            onChange={(e) => setEditPostForm({ ...editPostForm, title: e.target.value })}
            className="post-title-input"
          />
          
          <div className="project-type-selector">
            <label>Project Type:</label>
            <select
              value={editPostForm.projectType}
              onChange={(e) => setEditPostForm({ ...editPostForm, projectType: e.target.value })}
              className="project-type-dropdown"
            >
              <option value="individual">Individual Project</option>
              <option value="group">Group Project</option>
            </select>
          </div>
          
          <div className="project-type-selector">
            <label>Associated Class (Optional):</label>
            <select
              value={editPostForm.associatedClass || ''}
              onChange={(e) => setEditPostForm({ ...editPostForm, associatedClass: e.target.value })}
              className="project-type-dropdown"
            >
              <option value="">No class association</option>
              {studentClasses
                .filter(sc => sc.studentId === currentUser.uid)
                .filter(sc => {
                  const classType = classTypes.find(ct => ct.id === sc.classTypeId);
                  return classType?.name === 'Independent Study' || 
                         classType?.name === 'Service Experience' || 
                         classType?.name === 'Work Experience';
                })
                .map(classItem => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.className} - {classItem.grade}th Grade
                  </option>
                ))}
            </select>
          </div>
          
          <div className="project-type-selector">
            <label>Project Description *:</label>
            <textarea
              placeholder="Brief description of your project (required)"
              value={editPostForm.projectDescription}
              onChange={(e) => setEditPostForm({ ...editPostForm, projectDescription: e.target.value })}
              rows="3"
            />
          </div>
          
          <textarea
            placeholder="Your reflection..."
            value={editPostForm.content}
            onChange={(e) => setEditPostForm({ ...editPostForm, content: e.target.value })}
            rows="6"
          />
          
          <TagJustificationSection
            newPost={editPostForm}
            newTagJustification={newTagJustificationEdit}
            setNewTagJustification={setNewTagJustificationEdit}
            tags={tags}
            handleAddTagJustification={onAddTagJustificationEdit}
            handleRemoveTagJustification={onRemoveTagJustificationEdit}
          />
          
          <LinkSection
            newPost={editPostForm}
            newLink={newLinkEdit}
            setNewLink={setNewLinkEdit}
            handleAddLink={onAddLinkEdit}
            handleRemoveLink={onRemoveLinkEdit}
          />
          
          <div className="edit-post-actions">
            <button onClick={onSaveEditPost} className="proposal-save-btn">
              Save
            </button>
            <button onClick={onCancelEditPost} className="proposal-cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      ) : isExpanded ? (
        // Regular post display (expanded)
        <>
          {post.projectDescription && (
            <>
              <div className="post-section-title">Project Description</div>
              <div className="post-project-description">
                {post.projectDescription}
              </div>
            </>
          )}
          <div className="post-section-title">Reflection</div>
          <div className="post-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
              components={{
                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>,
                img: ({ node, ...props }) => <img {...props} alt="" className="max-w-full h-auto rounded" />
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {(post.links || []).length > 0 && (
            <div className="post-links">
              <strong>Artefacts:</strong>
              {(post.links || []).map((link, i) => (
                <div key={i} className="post-link-item">
                  <i className="fa-solid fa-link"></i>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.title}
                  </a>
                </div>
              ))}
            </div>
          )}

          {(post.tagJustifications || []).length > 0 && (
            <div className="post-tag-justifications">
              <strong>Tag Justifications:</strong>
              {(post.tagJustifications || []).map((item, i) => (
                <div key={i} className="post-tag-justification-item">
                  <i className="fa-solid fa-tag"></i>
                  <span><strong>{item.tag}:</strong> {item.justification}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}

      {/* Only show feedback sections when expanded */}
      {isExpanded && (post.tagJustifications || []).length > 0 && (
        <div className="comments-section">
          <h4>Specific Feedback</h4>
          {post.tagJustifications.map(item => {
            const key = `${post.id}__${item.tag}`;
            const tfList = getPostTagFeedbacks(post.id, item.tag);
            const entry = newTagFeedback[key] || { comment: '', score: '' };
            return (
              <div key={item.tag} className="feedback-section-item">
                <div className="feedback-section-header">
                  {item.tag}
                </div>
                {(tfList || []).length === 0 ? (
                  <div className="comment">No feedback yet.</div>
                ) : (
                  (tfList || []).map(tf => {
                    const isOwnFeedback = currentUserRole === 'teacher' && tf.teacherId === currentUser.uid;
                    const isEditingThisFeedback = editingFeedbackId === tf.id;
                    
                    if (isEditingThisFeedback) {
                      return (
                        <div key={tf.id} className="comment editing-feedback">
                          <div className="edit-feedback-form">
                            <select
                              value={editingFeedbackForm.score || ''}
                              onChange={(e) => setEditingFeedbackForm({ 
                                ...editingFeedbackForm, 
                                score: e.target.value 
                              })}
                            >
                              <option value="">Score 1–5</option>
                              <option value="1">1</option>
                              <option value="2">2</option>
                              <option value="3">3</option>
                              <option value="4">4</option>
                              <option value="5">5</option>
                            </select>
                            <textarea
                              placeholder="Comment (optional)"
                              value={editingFeedbackForm.comment || ''}
                              onChange={(e) => setEditingFeedbackForm({ 
                                ...editingFeedbackForm, 
                                comment: e.target.value 
                              })}
                              rows="2"
                            />
                            <div className="feedback-actions">
                              <button 
                                className="proposal-save-btn"
                                onClick={() => onSaveFeedback(tf.id)}
                              >
                                Save
                              </button>
                              <button 
                                className="proposal-cancel-btn"
                                onClick={onCancelEditFeedback}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div key={tf.id} className="comment">
                        <strong>{tf.teacherName}</strong>
                        {tf.score ? ` scored ${tf.score}` : ''}
                        {tf.comment ? ` — ${tf.comment}` : ''}
                        <span className="comment-time">{new Date(tf.timestamp).toLocaleString()}</span>
                        {isOwnFeedback && (
                          <div className="feedback-actions">
                            <button
                              className="proposal-edit-btn"
                              onClick={() => onEditFeedback(tf)}
                            >
                              Edit
                            </button>
                            <button
                              className="proposal-reject-btn"
                              onClick={() => onDeleteFeedback(tf.id)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                {currentUserRole === 'teacher' && (() => {
                  // Check if current teacher has already given feedback for this skill
                  const teacherHasGivenFeedback = tfList.some(tf => tf.teacherId === currentUser.uid);
                  
                  if (teacherHasGivenFeedback) {
                    return (
                      <div className="feedback-already-given">
                        <span className="feedback-check-text">
                          ✓ You have already provided feedback for this skill
                        </span>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="add-comment">
                      <select
                        value={entry.score || ''}
                        onChange={(e) => setNewTagFeedback({
                          ...newTagFeedback,
                          [key]: { ...entry, score: e.target.value }
                        })}
                      >
                        <option value="">Score 1–5</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                      </select>
                      <textarea
                        placeholder={`Comment for ${item.tag} (optional)`}
                        value={entry.comment || ''}
                        onChange={(e) => setNewTagFeedback({
                          ...newTagFeedback,
                          [key]: { ...entry, comment: e.target.value }
                        })}
                        rows="2"
                      />
                      <button onClick={() => onAddTagFeedback(post.id, item.tag)}>Save</button>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {isExpanded && currentUserRole === 'teacher' && (
        <div className="comments-section">
          <h4>Add Feedback For Another Skill</h4>
          {(() => {
            const extra = teacherExtraFeedback[post.id] || { tag: '' };
            const feedbackKey = `${post.id}__${extra.tag}`;
            const entry = newTagFeedback[feedbackKey] || { comment: '', score: '' };
            return (
              <div className="feedback-section-item">
                <div className="add-comment">
                  <div className="feedback-flex">
                    <select
                      value={extra.tag}
                      onChange={(e) => setTeacherExtraFeedback({
                        ...teacherExtraFeedback,
                        [post.id]: { ...extra, tag: e.target.value }
                      })}
                    >
                      <option value="">Select existing skill…</option>
                      {(tags || []).map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>

                  {extra.tag && (
                    <>
                      <select
                        value={entry.score || ''}
                        onChange={(e) => setNewTagFeedback({
                          ...newTagFeedback,
                          [feedbackKey]: { ...entry, score: e.target.value }
                        })}
                      >
                        <option value="">Score 1–5</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                      </select>
                      <textarea
                        placeholder={`Comment for ${extra.tag} (optional)`}
                        value={entry.comment || ''}
                        onChange={(e) => setNewTagFeedback({
                          ...newTagFeedback,
                          [feedbackKey]: { ...entry, comment: e.target.value }
                        })}
                        rows="2"
                      />
                      <button onClick={() => onAddTagFeedback(post.id, extra.tag)}>Save</button>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {isExpanded && (
      <div className="comments-section">
        <h4>General Feedback ({(getPostComments(post.id) || []).length})</h4>
        {getPostComments(post.id).map(comment => (
          <div key={comment.id} className="comment">
            <strong>{comment.username}:</strong> {comment.content}
            <span className="comment-time">{new Date(comment.timestamp).toLocaleString()}</span>
          </div>
        ))}

        {currentUserRole === 'teacher' && (
          <div className="add-comment">
            <textarea
              placeholder="Add a comment..."
              value={newComment[post.id] || ''}
              onChange={(e) => setNewComment({ ...newComment, [post.id]: e.target.value })}
              rows="2"
            />
            <button onClick={() => onAddComment(post.id)}>Comment</button>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

